export const START_RATING = 1200;

export function playerName(player) {
  if (!player) return "Unbekannt";
  const fullName = `${player.firstname || ""} ${player.lastname || ""}`.trim();
  return fullName || player.email || player.id || "Unbekannt";
}

export function evaluateSets(sets) {
  const played = sets
    .filter(set => set.home !== "" && set.away !== "")
    .map(set => ({ home: Number(set.home), away: Number(set.away) }));
  if (played.length < 2 || played.some(set => !Number.isInteger(set.home) || !Number.isInteger(set.away) || set.home === set.away || set.home < 0 || set.away < 0)) {
    throw new Error("Bitte mindestens zwei vollständige Sätze ohne Unentschieden eintragen.");
  }
  const homeSets = played.filter(set => set.home > set.away).length;
  const awaySets = played.filter(set => set.away > set.home).length;
  if (homeSets !== 2 && awaySets !== 2) throw new Error("Das Ergebnis braucht genau einen Sieger mit zwei gewonnenen Sätzen.");
  if (played.length === 3) {
    const firstTwoHome = played.slice(0, 2).filter(set => set.home > set.away).length;
    if (firstTwoHome === 0 || firstTwoHome === 2) throw new Error("Ein dritter Satz ist nach zwei gewonnenen Sätzen nicht mehr nötig.");
  }
  return {
    sets: played,
    winner: homeSets > awaySets ? "home" : "away",
    homeSets,
    awaySets,
    gameDifference: Math.abs(played.reduce((sum, set) => sum + set.home - set.away, 0)),
  };
}

export function calculateRanking(users, matches) {
  const table = Object.fromEntries(users.map(user => [user.id, {
    ...user, rating: START_RATING, games: 0, wins: 0, losses: 0,
  }]));
  const ordered = [...matches].filter(match => match.sets?.length && table[match.player1Id] && table[match.player2Id])
    .sort((a, b) => (a.playedAt?.seconds || new Date(a.playedAt || 0).getTime() / 1000) - (b.playedAt?.seconds || new Date(b.playedAt || 0).getTime() / 1000));

  ordered.forEach(match => {
    const p1 = table[match.player1Id], p2 = table[match.player2Id];
    const outcome = evaluateSets(match.sets);
    // Bei Mixed-Spielen wird für die Erwartungsrechnung ein moderater
    // Ausgleich von 100 Punkten angesetzt. Dadurch erhält eine Frau für
    // einen Sieg gegen einen Mann mehr und verliert bei einer Niederlage
    // weniger. Die tatsächlich erspielten Ranglistenpunkte bleiben erhalten.
    const mixedMatch = p1.gender && p2.gender && p1.gender !== p2.gender;
    const p1Adjustment = mixedMatch && p1.gender === "female" ? -100 : 0;
    const p2Adjustment = mixedMatch && p2.gender === "female" ? -100 : 0;
    const expected = 1 / (1 + Math.pow(10, ((p2.rating + p2Adjustment) - (p1.rating + p1Adjustment)) / 400));
    const actual = outcome.winner === "home" ? 1 : 0;
    const margin = Math.min(1.5, 1 + Math.abs(outcome.homeSets - outcome.awaySets) * .12 + outcome.gameDifference * .012);
    const k = p1.games < 10 || p2.games < 10 ? 40 : 28;
    let delta = Math.round(k * margin * (actual - expected));
    if (!delta) delta = actual ? 1 : -1;
    p1.rating += delta; p2.rating -= delta;
    p1.games++; p2.games++;
    if (actual) { p1.wins++; p2.losses++; } else { p2.wins++; p1.losses++; }
  });
  return Object.values(table).sort((a, b) => b.rating - a.rating || b.wins - a.wins || playerName(a).localeCompare(playerName(b)));
}

/* Ergebnisse werden über src/api.js serverseitig gespeichert. */
/*
export async function saveBookingResult(db, booking, rawSets, editorId) {
  if (!booking?.id || !booking?.userId || !booking?.opponentId) throw new Error("Für diese Buchung ist kein Gegner hinterlegt.");
  if (![booking.userId, booking.opponentId].includes(editorId)) throw new Error("Nur die beiden Spieler dürfen dieses Ergebnis bearbeiten.");
  const outcome = evaluateSets(rawSets);
  const bookingRef = doc(db, "bookings", booking.id);
  const matchRef = doc(db, "matches", `booking-${booking.id}`);
  await runTransaction(db, async transaction => {
    const bookingSnap = await transaction.get(bookingRef);
    if (!bookingSnap.exists()) throw new Error("Die Buchung existiert nicht mehr.");
    const current = bookingSnap.data();
    if (![current.userId, current.opponentId].includes(editorId)) throw new Error("Nur die beiden Spieler dürfen dieses Ergebnis bearbeiten.");
    const result = { sets: outcome.sets, winnerId: outcome.winner === "home" ? current.userId : current.opponentId, updatedBy: editorId, updatedAt: serverTimestamp() };
    transaction.set(matchRef, {
      player1Id: current.userId, player2Id: current.opponentId, sets: outcome.sets,
      winnerId: result.winnerId, bookingId: booking.id, source: "booking",
      playedAt: new Date(`${current.date}T${String(current.hour).padStart(2, "0")}:00`),
      updatedBy: editorId, updatedAt: serverTimestamp(), createdAt: serverTimestamp(),
    }, { merge: true });
    transaction.update(bookingRef, { result });
  });
}

// Rückwärtskompatibler Name für den Buchungskalender.
export const saveRankedResult = saveBookingResult;

export async function saveManualMatch(db, { matchId, player1Id, player2Id, sets, playedAt }, editorId) {
  if (!player1Id || !player2Id || player1Id === player2Id) throw new Error("Bitte zwei unterschiedliche Spieler auswählen.");
  if (![player1Id, player2Id].includes(editorId)) throw new Error("Du kannst nur Spiele eintragen, an denen du selbst beteiligt bist.");
  const outcome = evaluateSets(sets);
  const data = {
    player1Id, player2Id, sets: outcome.sets,
    winnerId: outcome.winner === "home" ? player1Id : player2Id,
    source: "manual", playedAt: playedAt ? new Date(`${playedAt}T12:00:00`) : new Date(),
    updatedBy: editorId, updatedAt: serverTimestamp(),
  };
  if (matchId) {
    const ref = doc(db, "matches", matchId);
    const snap = await getDoc(ref);
    if (!snap.exists() || ![snap.data().player1Id, snap.data().player2Id].includes(editorId)) throw new Error("Nur die beiden Spieler dürfen dieses Ergebnis bearbeiten.");
    await setDoc(ref, data, { merge: true });
  } else {
    await addDoc(collection(db, "matches"), { ...data, createdAt: serverTimestamp() });
  }
}
*/
